import React from 'react';
import { Plus } from 'lucide-react';
import { Pagination } from '../../services/CarTripService';
import { GroundTripCard } from '../ground-trip/GroundTripCard';
import { GroundTripForm } from '../ground-trip/GroundTripForm';
import { useGroundTripService } from '../../hooks/useGroundTripService';
import type { GroundMode, GroundTrip } from '../../types/ground';
import type { TranslationKeys } from '../../translations';

const LoadingSpinner = () => (
    <div className="flex justify-center items-center p-4">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
    </div>
);

type GroundService = ReturnType<typeof useGroundTripService>;

export type GroundTabPanelProps = {
    mode: GroundMode;
    svc: GroundService;
    t: TranslationKeys;
    language: string;
    currency: string;
    onView?: (trip: GroundTrip) => void;
};

export const GroundTabPanel: React.FC<GroundTabPanelProps> = ({
    mode,
    svc,
    t,
    language,
    currency,
    onView,
}) => {
    const addLabel =
        mode === 'bus' ? t.addBusTrip : mode === 'ferry' ? t.addFerryTrip : t.addTrainTrip;
    const emptyLabel =
        mode === 'bus' ? t.busesEmpty : mode === 'ferry' ? t.ferriesEmpty : t.trainsEmpty;
    return (
        <div className="space-y-2">
            <div className="mb-3 hidden md:flex flex-wrap items-start justify-end gap-2">
                <button
                    type="button"
                    onClick={svc.openNew}
                    className={`hidden md:inline-flex items-center justify-center shrink-0 min-h-9 min-w-9 px-2.5 py-1.5 rounded-xl text-sm font-semibold text-white shadow-sm ${svc.theme.addBtn}`}
                    title={addLabel}
                    aria-label={addLabel}
                >
                    <Plus className="h-4 w-4" />
                </button>
            </div>
            {svc.loading ? (
                <LoadingSpinner />
            ) : svc.trips.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-stone-300 dark:border-slate-600 px-6 py-12 text-center text-sm text-stone-500 dark:text-slate-400">
                    {emptyLabel}
                </div>
            ) : (
                <>
                    {svc.pageTrips.map((trip: GroundTrip) => (
                        <GroundTripCard
                            key={trip.id}
                            trip={trip}
                            selected={trip.id === svc.selectedId}
                            onSelect={() => svc.setSelectedId(prev => (prev === trip.id ? null : trip.id))}
                            onView={onView}
                            onEdit={svc.openEdit}
                            onDelete={gt => void svc.remove(gt)}
                        />
                    ))}
                    {svc.trips.length > svc.perPage && (
                        <Pagination
                            currentPage={svc.page}
                            totalPages={svc.totalPages}
                            onPageChange={(page: number) => svc.setPage(page)}
                            onPrevPage={() => svc.setPage(p => Math.max(1, p - 1))}
                            onNextPage={() => svc.setPage(p => Math.min(svc.totalPages, p + 1))}
                        />
                    )}
                </>
            )}
            <GroundTripForm
                mode={mode}
                open={svc.showForm}
                onClose={svc.closeForm}
                draft={svc.draft}
                setDraft={svc.setDraft}
                onSave={svc.save}
                isEditing={svc.isEditing}
                language={language}
                currency={currency}
            />
        </div>
    );
};
